import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, Globe, Crown, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/store";
import { Stars } from "@/components/Stars";
import ShareButtons from "@/components/ShareButtons";
import ListingCard from "@/components/ListingCard";
import { ReportButton } from "@/components/ReportButton";

const FLAG = { BR: "🇧🇷 Brazil", AU: "🇦🇺 Australia", US: "🇺🇸 United States" };

export default function PublicProfile() {
  const { id } = useParams();
  const { t } = useApp();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/users/${id}/public`).then(({ data }) => setP(data)).catch(() => setP(false)); }, [id]);

  if (p === null) return <div className="py-24 text-center text-gray-400">Loading…</div>;
  if (p === false) return <div className="py-24 text-center text-gray-400">User not found.</div>;

  const since = p.member_since ? new Date(p.member_since).getFullYear() : "";

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <div className="flex flex-wrap items-center gap-5" data-testid="public-profile">
        <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-[#002FA7] font-display text-3xl font-extrabold text-white">
          {p.picture ? <img src={p.picture} alt="" className="h-full w-full object-cover" /> : (p.name || "?")[0]}
        </div>
        <div>
          <h1 className="flex flex-wrap items-center gap-2 font-display text-3xl font-extrabold tracking-tight" data-testid="public-name">
            {p.name}
            {p.verified && <span data-testid="public-verified-badge" className="inline-flex items-center gap-1 rounded-full bg-[#002FA7] px-2.5 py-1 text-xs font-bold text-white"><BadgeCheck size={13} /> {t("verified_user")}</span>}
            {p.phone_verified && <span data-testid="public-phone-verified" className="inline-flex items-center gap-1 rounded-full bg-[#155724]/10 px-2.5 py-1 text-xs font-bold text-[#155724]"><Smartphone size={13} /> {t("phone_verified")}</span>}
            {p.is_pro && <span data-testid="public-pro-badge" className="inline-flex items-center gap-1 rounded-full bg-[#0A0B10] px-2.5 py-1 text-xs font-bold text-yellow-300"><Crown size={13} /> {t("pro_business")}</span>}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            {p.rating.avg != null ? (
              <span className="flex items-center gap-1.5"><Stars value={p.rating.avg} size={15} /> <b className="text-gray-700">{p.rating.avg}</b> ({p.rating.count})</span>
            ) : <span>{t("no_reviews")}</span>}
            <span className="flex items-center gap-1"><Globe size={13} /> {FLAG[p.country] || p.country}</span>
            {since && <span>· Member since {since}</span>}
          </div>
        </div>
      </div>

      <div className="mt-6"><ShareButtons url={`${process.env.REACT_APP_BACKEND_URL}/api/share/user/${id}`} text={`Check out ${p.name} on RentGlobe`} label="Share this profile" /></div>
      <div className="mt-3"><ReportButton userId={p.user_id} testPrefix="report-user" /></div>

      <h2 className="mb-4 mt-10 font-display text-xl font-bold">{t("reviews")}</h2>
      {p.reviews.length === 0 ? <p className="text-sm text-gray-400">{t("no_reviews")}</p> : (
        <div className="space-y-3" data-testid="public-reviews">
          {p.reviews.map((r) => (
            <div key={r.review_id} className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-900">{r.reviewer_name}<span className="ml-2 text-xs font-normal uppercase tracking-widest text-gray-400">as {r.role}</span></span>
                <Stars value={r.rating} size={14} />
              </div>
              {r.comment && <p className="mt-1 text-sm text-gray-600">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-10 font-display text-xl font-bold">Listings ({p.listings.length})</h2>
      {p.listings.length === 0 ? <p className="text-sm text-gray-400">No active listings.</p> : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-listings">
          {p.listings.map((it, i) => <ListingCard key={it.listing_id} item={it} index={i} />)}
        </div>
      )}
    </div>
  );
}
